import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { persistReducer } from 'redux-persist'
import storage from 'redux-persist/lib/storage'

import { AvailableLocale } from '@/i18n'

import { selectIndividualAction, signOutAction } from '../actions'

export type SettingsState = {
  selectedIndividualId?: string
  featureAcronymTooltips: boolean
  isDeveloper: boolean
  locale: AvailableLocale | 'auto'
  selectedErrandId?: string
  /**
   * When the user last opened Hitta stöd, per individual (ISO). Purely local —
   * the "changed" dot is derived from `lastRun`, which only tells us what moved
   * since the previous generation, not what this user has already seen. Keyed by
   * individual so switching person doesn't clear someone else's dot.
   */
  servicesLastSeen: Record<string, string>
}

const initialState: SettingsState = {
  featureAcronymTooltips: true,
  isDeveloper: false,
  locale: 'auto',
  selectedErrandId: undefined,
  selectedIndividualId: undefined,
  servicesLastSeen: {},
}

const key = 'settings'

const SettingsSlice = createSlice({
  extraReducers: builder => {
    builder.addCase(selectIndividualAction, (state, action) => {
      state.selectedIndividualId = action.payload
      state.selectedErrandId = undefined
    })
    builder.addCase(signOutAction, state => {
      state.selectedIndividualId = initialState.selectedIndividualId
      state.selectedErrandId = initialState.selectedErrandId
    })
  },
  initialState,
  name: key,
  reducers: {
    markServicesSeen(state, action: PayloadAction<{ individualId: string; at: string }>) {
      // Guard: state persisted before this field existed rehydrates without it.
      if (!state.servicesLastSeen) state.servicesLastSeen = {}
      state.servicesLastSeen[action.payload.individualId] = action.payload.at
    },
    setIsDeveloper(state, action: PayloadAction<boolean>) {
      state.isDeveloper = action.payload
    },
    setLocale(state, action: PayloadAction<AvailableLocale | 'auto'>) {
      state.locale = action.payload
    },
    setSelectedErrandId(state, action: PayloadAction<string | undefined>) {
      state.selectedErrandId = action.payload
    },
    toggleAcronymTooltips(state, action: PayloadAction<boolean | undefined>) {
      state.featureAcronymTooltips = action.payload ?? !state.featureAcronymTooltips
    },
  },
})

export const SettingsReducer = persistReducer(
  {
    key,
    storage,
    whitelist: [
      'selectedIndividualId',
      'locale',
      'isDeveloper',
      'featureAcronymTooltips',
      'servicesLastSeen',
    ],
  },
  SettingsSlice.reducer,
)

export const SettingsActions = SettingsSlice.actions
